import React, { useState, useEffect } from 'react';
import { Send, Home, Palette, Ruler, Calendar } from 'lucide-react';
import './StartProject.css';
import { useNavigate } from 'react-router-dom';
import Header from "./Header";
import Footer from "./Footer";
import Chatbot from '../components/Chatbot';
import emailjs from 'emailjs-com';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StartProject = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    location: '',
    timeline: '',
    description: '',
    style: ''
  });
  const navigate = useNavigate();
  const [heroLoaded, setHeroLoaded] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load hero image
  useEffect(() => {
    const img = new Image();
    img.src = require('../assets/Startproj.webp');
    img.onload = () => {
      setHeroLoaded(true);
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      projectType: formData.projectType,
      location: formData.location || 'Not specified',
      timeline: formData.timeline || 'Not specified',
      style: formData.style || 'Not specified',
      description: formData.description,
      time: new Date().toLocaleString()
    };

    emailjs.send("service_j3icm5h", "template_ynimzlu", templateParams, "LsrTno83fC_TipZ0n")
      .then((response) => {
        console.log("✅ Email sent successfully!", response.status, response.text);
        toast.success("Thank you! Your project details have been submitted. We'll contact you soon.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          location: '',
          timeline: '',
          description: '',
          style: ''
        });
      }, (error) => {
        console.error("❌ Failed to send email:", error);
        toast.error("Error sending your project details. Please try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="start-project">
      <Header />
      <style>
        {`
          .Toastify__toast {
            background-color: #02331F;
            color: white;
            border-radius: 8px;
            font-size: 14px;
          }
          .Toastify__toast--success {
            background-color: #02331F;
          }
          .Toastify__toast--error {
            background-color: #7F1D1D;
          }
          .Toastify__progress-bar {
            background-color: #A47336;
          }
          .Toastify__close-button {
            color: white;
            opacity: 0.8;
          }
          .Toastify__close-button:hover {
            opacity: 1;
          }
        `}
      </style>
      <ToastContainer />
      <section
        className="project-hero"
        style={
          heroLoaded
            ? {
                backgroundImage: `url(${require('../assets/Startproj.webp')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : { backgroundColor: '#e5e7eb' }
        }
      >
        <div className="hero-content">
          <h1>Start Your Dream Project</h1>
          <p>Let's bring your vision to life with our expert interior design services</p>
        </div>
        <div className="hero-overlay"></div>
      </section>

      <section className="services-overview">
        <div className="container">
          <h2 style={{ color: "#006039" }}>Our Services</h2>
          <div className="services-grid" style={{ display: 'flex' }}>
            <div className="service-card">
              <Home className="service-icon" />
              <h3>Complete Home Design</h3>
              <p>Full interior design services from concept to completion</p>
            </div>
            <div className="service-card">
              <Palette className="service-icon" />
              <h3>Color Consultation</h3>
              <p>Expert color schemes to transform your space</p>
            </div>
            <div className="service-card">
              <Ruler className="service-icon" />
              <h3>Space Planning</h3>
              <p>Optimize your layout for maximum functionality</p>
            </div>
            <div className="service-card">
              <Calendar className="service-icon" />
              <h3>Project Management</h3>
              <p>End-to-end project coordination and timeline management</p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Tell Us About Your Project</h2>
              <p>Fill out the form below and we'll get back to you within 24 hours</p>
            </div>
            
            <form onSubmit={handleSubmit} className="project-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="projectType">Project Type *</label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Project Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="renovation">Renovation</option>
                    <option value="consultation">Consultation Only</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="location">Project Location – Mention the City and Area *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter city and area (e.g., New York, Manhattan)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="timeline">Timeline</label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Timeline</option>
                    <option value="asap">ASAP</option>
                    <option value="1-3months">1-3 months</option>
                    <option value="3-6months">3-6 months</option>
                    <option value="6months-plus">6+ months</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="style">Preferred Style</label>
                <select
                  id="style"
                  name="style"
                  value={formData.style}
                  onChange={handleInputChange}
                >
                  <option value="">Select Style Preference</option>
                  <option value="modern">Modern</option>
                  <option value="contemporary">Contemporary</option>
                  <option value="traditional">Traditional</option>
                  <option value="scandinavian">Scandinavian</option>
                  <option value="industrial">Industrial</option>
                  <option value="bohemian">Bohemian</option>
                  <option value="minimalist">Minimalist</option>
                  <option value="eclectic">Eclectic</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Project Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="Tell us about your vision, requirements, and any specific needs..."
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-btn">
                <Send size={20} />
                Send Project Details
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer className="footer--home-green" />
      <Chatbot />
    </div>
  );
};

export default StartProject;
